import {
  Badge,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Subtitle2,
  TabList,
  Tab,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  Add16Regular,
  Copy16Regular,
  Delete16Regular,
  Edit16Regular,
  MoreHorizontal16Regular,
} from "@fluentui/react-icons";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar } from "@/components/app-dialog";
import { FuiButton, FuiDropdown } from "@/components/fui-control";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import type { DocumentBlock, DocumentKind } from "@/engine/types";
import { canWorkspace, useEffectiveWorkspaceRole } from "@/features/access/workspace-access";
import { formatDate } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { recordCode } from "@/store/record-ops";
import { newId } from "@/store/tree-ops";
import { isBlankBlocks } from "./doc-blocks";
import type { DocEditorHandle } from "./DocBlockEditor";
import { mentionChipLabel, mentionTargetAlive, type LinkCatalog } from "./doc-links";
import { DOCUMENT_KIND_LABEL, DOCUMENT_KINDS } from "./doc-model";
import {
  blocksFromOffer,
  blocksFromSections,
  cloneBlocksWithNewIds,
  editorTemplateOffers,
  sectionsFromBlocks,
  type DocTemplateOffer,
  type DocTemplateSection,
  type WorkspaceDocTemplateDraft,
} from "./doc-templates";

const DocBlockEditor = lazy(() => import("./DocBlockEditor").then((mod) => ({ default: mod.DocBlockEditor })));

/** 열릴 때의 모습. 비우면 목록과 미리보기. */
export type DocTemplateModalIntent =
  | { type: "browse"; selectId?: string }
  | { type: "create" }
  | { type: "edit"; id: string }
  | { type: "copy"; id: string }
  | { type: "draft"; draft: WorkspaceDocTemplateDraft };

type Draft = {
  id?: string;
  name: string;
  kind: DocumentKind;
  blocks: DocumentBlock[];
  updatedAt?: string;
  updatedBy?: string;
  createdAt?: string;
  createdBy?: string;
};

const useStyles = makeStyles({
  layout: {
    display: "grid",
    gridTemplateColumns: "250px minmax(0, 1fr)",
    height: "100%",
    minHeight: 0,
  },
  side: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: "8px",
    gap: "6px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    overflow: "auto",
    flex: "1 1 auto",
    minHeight: 0,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: tokens.borderRadiusMedium,
    paddingLeft: "8px",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  itemOn: { backgroundColor: tokens.colorNeutralBackground1Selected },
  itemMain: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    flex: "1 1 auto",
    minWidth: 0,
    padding: "6px 0",
    border: "none",
    background: "none",
    font: "inherit",
    color: tokens.colorNeutralForeground1,
    textAlign: "left",
    cursor: "pointer",
  },
  itemName: { maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: tokens.fontSizeBase300 },
  itemMeta: { display: "flex", gap: "4px", alignItems: "center", color: tokens.colorNeutralForeground3, flexWrap: "wrap" },
  popover: { zIndex: 100, pointerEvents: "auto" },
  main: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    paddingLeft: "16px",
    height: "100%",
  },
  pane: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    gap: "10px",
  },
  paneHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  titleInput: {
    width: "100%",
    border: "none",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    letterSpacing: "-0.02em",
    padding: "4px 0 6px",
    boxSizing: "border-box",
    transitionProperty: "border-color",
    transitionDuration: tokens.durationFaster,
    "::placeholder": {
      color: tokens.colorNeutralForeground4,
    },
    ":focus": { outline: "none" },
    ":focus-visible": {
      outline: "none",
      borderBottomColor: tokens.colorStrokeFocus2,
    },
  },
  docMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minHeight: "28px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  docMetaItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  dot: {
    color: tokens.colorNeutralStroke1,
    userSelect: "none",
  },
  scroll: {
    overflowY: "auto",
    flex: "1 1 auto",
    minHeight: 0,
    paddingRight: "8px",
  },
  editorHost: {
    display: "flex",
    flexDirection: "column",
    minHeight: "360px",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "16px 20px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  h1: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, margin: 0, marginTop: "6px" },
  note: { color: tokens.colorNeutralForeground3, margin: 0 },
  todo: { display: "flex", alignItems: "center", gap: "8px", color: tokens.colorNeutralForeground3 },
  box: { width: "14px", height: "14px", border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: "3px", flexShrink: 0 },
  bullet: { color: tokens.colorNeutralForeground3, margin: 0 },
  table: { borderCollapse: "collapse", width: "100%", fontSize: tokens.fontSizeBase200 },
  th: { border: `1px solid ${tokens.colorNeutralStroke2}`, padding: "4px 8px", textAlign: "left", backgroundColor: tokens.colorNeutralBackground2 },
  td: { border: `1px solid ${tokens.colorNeutralStroke2}`, padding: "4px 8px", color: tokens.colorNeutralForeground3, height: "24px" },
  empty: { color: tokens.colorNeutralForeground3, padding: "16px 8px" },
  fail: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
  },
});

function defaultTemplateBlocks(): DocumentBlock[] {
  return [
    {
      id: newId("bk"),
      type: "heading",
      props: { level: 1 },
      content: [{ type: "text", text: "개요", styles: {} }],
    },
    {
      id: newId("bk"),
      type: "paragraph",
      content: [],
    },
  ];
}

function draftFromOffer(offer: DocTemplateOffer, asCopy = false): Draft {
  const blocks = blocksFromOffer(offer);
  if (asCopy || offer.origin === "system") {
    return {
      id: newId("dt"),
      name: `${offer.name} 복사`,
      kind: offer.kind,
      blocks: cloneBlocksWithNewIds(blocks),
      updatedAt: offer.updatedAt,
      updatedBy: offer.updatedBy,
    };
  }
  return {
    id: offer.id,
    name: offer.name,
    kind: offer.kind,
    blocks,
    updatedAt: offer.updatedAt,
    updatedBy: offer.updatedBy,
    createdAt: offer.createdAt,
    createdBy: offer.createdBy,
  };
}

function draftFrom(source: WorkspaceDocTemplateDraft): Draft {
  const blocks = source.blocks && source.blocks.length > 0
    ? cloneBlocksWithNewIds(source.blocks)
    : source.sections && source.sections.length > 0
      ? blocksFromSections(source.sections)
      : defaultTemplateBlocks();
  return {
    ...(source.id ? { id: source.id } : { id: newId("dt") }),
    name: source.name,
    kind: source.kind,
    blocks: blocks.length > 0 ? blocks : defaultTemplateBlocks(),
    updatedBy: source.updatedBy,
    createdAt: source.createdAt,
    createdBy: source.createdBy,
  };
}

function TemplatePreview({ offer }: { offer: DocTemplateOffer }) {
  const styles = useStyles();
  return (
    <div className={styles.paper} aria-label={`${offer.name} 미리보기`}>
      {offer.sections.map((section, index) => (
        <div key={`${section.title}-${index}`}>
          <h3 className={styles.h1}>{section.title}</h3>
          {section.note ? <p className={styles.note}>{section.note}</p> : null}
          {section.table ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  {section.table.map((column) => (
                    <th key={column} className={styles.th}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {section.table.map((column, i) => (
                    <td key={column} className={styles.td}>
                      {section.example?.[i] ?? ""}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          ) : null}
          {section.todos?.map((todo, i) => (
            <div key={`${todo}-${i}`} className={styles.todo}>
              <span className={styles.box} />
              <span>{todo}</span>
            </div>
          ))}
          {section.list ? <p className={styles.bullet}>• </p> : null}
        </div>
      ))}
    </div>
  );
}

const noopMentionLabel = () => "";
const noopMentionAlive = () => false;
const noop = () => {};
const emptyMentionItems = () => [];
const resolveUrl = async (url: string) => url;
const uploadFile = async () => "";

function initialDraft(intent: DocTemplateModalIntent, offers: readonly DocTemplateOffer[]): Draft | null {
  if (intent.type === "create") {
    return {
      id: newId("dt"),
      name: "",
      kind: "material",
      blocks: defaultTemplateBlocks(),
    };
  }
  if (intent.type === "draft") return draftFrom(intent.draft);
  if (intent.type === "edit" || intent.type === "copy") {
    const offer = offers.find((item) => item.id === intent.id);
    if (!offer) return null;
    return draftFromOffer(offer, intent.type === "copy");
  }
  return null;
}

/**
 * 템플릿 관리 모달. 왼쪽 목록, 오른쪽 미리보기 또는 편집.
 * Docs와 동일한 BlockNote 에디터 환경을 제공하며, 수정자 및 수정일시를 표시한다.
 */
export function DocTemplateModal({
  open,
  onClose,
  intent = { type: "browse" },
  applyLabel = "이 템플릿으로 시작",
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  intent?: DocTemplateModalIntent;
  applyLabel?: string;
  onApply?: (offer: DocTemplateOffer) => void;
}) {
  const workspaceTemplates = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.docTemplates);
  const role = useEffectiveWorkspaceRole();
  const canManage = canWorkspace(role, "workspace.settings.update");
  const offers = useMemo(() => editorTemplateOffers(workspaceTemplates ?? []), [workspaceTemplates]);
  if (!open) return null;
  return <ModalBody key={JSON.stringify(intent)} onClose={onClose} intent={intent} applyLabel={applyLabel} onApply={onApply} offers={offers} canManage={canManage} />;
}

function ModalBody({
  onClose,
  intent,
  applyLabel,
  onApply,
  offers,
  canManage,
}: {
  onClose: () => void;
  intent: DocTemplateModalIntent;
  applyLabel: string;
  onApply?: (offer: DocTemplateOffer) => void;
  offers: readonly DocTemplateOffer[];
  canManage: boolean;
}) {
  const styles = useStyles();
  const layer = useDialogLayer();
  const currentUser = useCatalogStore((s) => s.profile?.displayName) || "나";
  const [filter, setFilter] = useState<"all" | "system" | "workspace">("all");
  const [selectedId, setSelectedId] = useState<string>(intent.type === "browse" && intent.selectId ? intent.selectId : (offers[0]?.id ?? ""));
  const [draft, setDraft] = useState<Draft | null>(() => (canManage ? initialDraft(intent, offers) : null));
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [pasteError, setPasteError] = useState("");

  const editorRef = useRef<DocEditorHandle>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const shown = offers.filter((offer) => filter === "all" || offer.origin === filter);
  const selected = shown.find((offer) => offer.id === selectedId) ?? shown[0] ?? null;

  const previewBlocks = useMemo(
    () => (selected ? blocksFromOffer(selected) : []),
    [selected?.id, selected?.updatedAt, selected?.blocks, selected?.sections],
  );

  const mentionItems = useCallback((query: string) => {
    const q = query.trim().toLowerCase();
    const state = useProjectStore.getState();
    const items: Array<{ title: string; targetKind: string; targetId: string; onItemClick: () => void }> = [];
    const push = (targetKind: string, targetId: string, title: string, code?: string) => {
      const label = [code, title].filter(Boolean).join(" ");
      if (q && !label.toLowerCase().includes(q)) return;
      items.push({ title: label, targetKind, targetId, onItemClick: () => {} });
    };
    for (const task of state.project?.executionTasks ?? []) push("executionTask", task.id, task.title || "제목 없음");
    for (const task of state.project?.tasks ?? []) push("scheduleTask", task.id, task.name || "이름 없는 작업", state.derived?.schedule?.tasks?.get(task.id)?.wbs);
    for (const record of state.project?.records ?? []) push("record", record.id, record.title || "제목 없음", recordCode(record));
    return items.slice(0, 12);
  }, []);

  const mentionLabel = useCallback((kind: string, id: string) => {
    const state = useProjectStore.getState();
    if (!state.project?.id) return "";
    const catalog: LinkCatalog = {
      executionTasks: state.project.executionTasks,
      tasks: state.project.tasks,
      records: state.project.records,
    };
    return mentionChipLabel(kind, id, catalog, state.project.documentLinks);
  }, []);

  const mentionAlive = useCallback((kind: string, id: string) => {
    const state = useProjectStore.getState();
    if (!state.project?.id) return false;
    const catalog: LinkCatalog = {
      executionTasks: state.project.executionTasks,
      tasks: state.project.tasks,
      records: state.project.records,
    };
    return mentionTargetAlive(kind, id, catalog);
  }, []);

  const startEdit = (offer: DocTemplateOffer) => {
    setSelectedId(offer.id);
    setPasteError("");
    setDraft(draftFromOffer(offer, false));
  };
  const startCopy = (offer: DocTemplateOffer) => {
    setSelectedId(offer.id);
    setPasteError("");
    setDraft(draftFromOffer(offer, true));
  };

  const ready = !!draft && draft.name.trim().length > 0 && draft.blocks.length > 0 && !isBlankBlocks(draft.blocks);

  const save = useCallback(() => {
    if (!draft) return;
    const author = useCatalogStore.getState().profile?.displayName || "나";
    const sections = sectionsFromBlocks(draft.blocks);
    const savedId = draft.id || newId("dt");
    const ok = useCatalogStore.getState().saveWorkspaceDocTemplate({
      id: savedId,
      name: draft.name,
      kind: draft.kind,
      sections: sections.length > 0 ? sections : [{ title: "개요" }],
      blocks: draft.blocks,
      updatedBy: author,
      createdAt: draft.createdAt,
      createdBy: draft.createdBy,
    });
    if (!ok) return;
    if (intent.type === "draft") {
      onClose();
      return;
    }
    setDraft(null);
    setSelectedId(savedId);
    setFilter((f) => (f === "system" ? "workspace" : f));
  }, [draft, intent.type, onClose]);

  const saveRef = useRef(save);
  saveRef.current = save;
  const readyRef = useRef(ready);
  readyRef.current = ready;

  useEffect(() => {
    if (!draft) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (readyRef.current) {
          saveRef.current();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft]);

  const title = draft ? (draft.id && offers.some((o) => o.id === draft.id) ? "템플릿 수정" : "새 템플릿") : "템플릿";

  return (
    <AppDialog
      open
      onClose={onClose}
      title={title}
      width="xl"
      height={640}
      footer={
        draft ? (
          <DialogActionsBar
            confirmLabel="저장"
            disabled={!ready}
            onCancel={() => {
              if (intent.type === "browse") setDraft(null);
              else onClose();
            }}
            onConfirm={save}
          />
        ) : (
          <>
            <FuiButton density="dialog" appearance="secondary" onClick={onClose}>
              닫기
            </FuiButton>
            {selected && canManage && selected.origin === "workspace" ? (
              <FuiButton density="dialog" appearance="subtle" icon={<Delete16Regular />} onClick={() => setPendingDelete(selected.id)}>
                삭제
              </FuiButton>
            ) : null}
            {selected && canManage ? (
              <FuiButton density="dialog" appearance="secondary" icon={<Copy16Regular />} onClick={() => startCopy(selected)}>
                복사하여 편집
              </FuiButton>
            ) : null}
            {selected && canManage && selected.origin === "workspace" ? (
              <FuiButton density="dialog" appearance="secondary" icon={<Edit16Regular />} onClick={() => startEdit(selected)}>
                수정
              </FuiButton>
            ) : null}
            {selected && onApply ? (
              <FuiButton density="dialog" appearance="primary" onClick={() => onApply(selected)}>
                {applyLabel}
              </FuiButton>
            ) : null}
          </>
        )
      }
    >
      <div className={styles.layout}>
        <div className={styles.side}>
          <TabList size="small" selectedValue={filter} onTabSelect={(_, data) => setFilter(data.value as typeof filter)} aria-label="템플릿 구분">
            <Tab value="all">전체</Tab>
            <Tab value="system">기본</Tab>
            <Tab value="workspace">워크스페이스</Tab>
          </TabList>
          <div className={styles.list} role="listbox" aria-label="템플릿 목록">
            {shown.length === 0 ? <Caption1 className={styles.empty}>템플릿이 없습니다.</Caption1> : null}
            {shown.map((offer) => (
              <div key={offer.id} className={mergeClasses(styles.item, selected?.id === offer.id && !draft && styles.itemOn)}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected?.id === offer.id}
                  className={styles.itemMain}
                  onClick={() => {
                    if (draft) return;
                    setSelectedId(offer.id);
                  }}
                  onDoubleClick={() => onApply?.(offer)}
                >
                  <span className={styles.itemName}>{offer.name}</span>
                  <span className={styles.itemMeta}>
                    <Badge size="small" appearance="tint" color={offer.origin === "system" ? "informative" : "brand"}>
                      {DOCUMENT_KIND_LABEL[offer.kind]}
                    </Badge>
                    <Caption1>
                      {offer.origin === "system"
                        ? "기본"
                        : offer.updatedBy
                          ? `수정 ${offer.updatedBy}`
                          : "워크스페이스"}
                    </Caption1>
                    {offer.updatedAt ? <Caption1>· {formatDate(offer.updatedAt, "numeric")}</Caption1> : null}
                  </span>
                </button>
                {canManage ? (
                  <Menu mountNode={layer} positioning={{ strategy: "fixed", position: "below", align: "end" }}>
                    <MenuTrigger disableButtonEnhancement>
                      <FuiButton appearance="subtle" density="dialog" icon={<MoreHorizontal16Regular />} aria-label={`${offer.name} 동작`} disabled={!!draft} />
                    </MenuTrigger>
                    <MenuPopover className={styles.popover}>
                      <MenuList>
                        {offer.origin === "workspace" ? (
                          <MenuItem icon={<Edit16Regular />} onClick={() => startEdit(offer)}>
                            수정
                          </MenuItem>
                        ) : null}
                        <MenuItem icon={<Copy16Regular />} onClick={() => startCopy(offer)}>
                          {offer.origin === "system" ? "복사하여 수정" : "복사"}
                        </MenuItem>
                        {offer.origin === "workspace" ? (
                          <MenuItem icon={<Delete16Regular />} onClick={() => setPendingDelete(offer.id)}>
                            삭제
                          </MenuItem>
                        ) : null}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                ) : null}
              </div>
            ))}
          </div>
          {canManage ? (
            <FuiButton
              appearance="subtle"
              density="dialog"
              icon={<Add16Regular />}
              disabled={!!draft}
              onClick={() => {
                setPasteError("");
                setDraft({
                  id: newId("dt"),
                  name: "",
                  kind: "material",
                  blocks: defaultTemplateBlocks(),
                });
              }}
            >
              새 템플릿 만들기
            </FuiButton>
          ) : null}
        </div>

        <div className={styles.main}>
          {draft ? (
            <div className={styles.pane}>
              <div className={styles.paneHeader}>
                <input
                  ref={nameInputRef}
                  className={styles.titleInput}
                  value={draft.name}
                  maxLength={80}
                  placeholder="템플릿 이름"
                  aria-label="템플릿 이름"
                  autoFocus
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== "ArrowDown") return;
                    event.preventDefault();
                    editorRef.current?.focusStart();
                  }}
                />

                <div className={styles.docMetaRow}>
                  <span className={styles.docMetaItem}>
                    <span>종류</span>
                    <FuiDropdown
                      density="dialog"
                      aria-label="문서 종류"
                      value={draft.kind}
                      options={DOCUMENT_KINDS.map((item) => ({ value: item, label: DOCUMENT_KIND_LABEL[item] }))}
                      onValueChange={(value) => {
                        if (value === "material" || value === "meeting" || value === "memo") setDraft({ ...draft, kind: value });
                      }}
                    />
                  </span>
                  <span className={styles.dot} aria-hidden>
                    ·
                  </span>
                  <span className={styles.docMetaItem}>
                    {draft.id && offers.some((o) => o.id === draft.id)
                      ? `수정자 ${currentUser}`
                      : `작성자 ${currentUser}`}
                  </span>
                  <span className={styles.dot} aria-hidden>
                    ·
                  </span>
                  <span className={styles.docMetaItem}>
                    {draft.updatedAt ? (
                      <>
                        이전 수정 {draft.updatedBy ? `${draft.updatedBy} · ` : ""}
                        {formatDate(draft.updatedAt, "dateTime")}
                      </>
                    ) : (
                      "새 템플릿"
                    )}
                  </span>
                </div>
              </div>

              {pasteError ? <Caption1 className={styles.fail}>{pasteError}</Caption1> : null}

              <div className={styles.scroll}>
                <div className={styles.editorHost}>
                  <Suspense fallback={<Caption1 className={styles.empty}>편집기를 여는 중...</Caption1>}>
                    <DocBlockEditor
                      key={`edit-${draft.id ?? "new"}`}
                      ref={editorRef}
                      docId={`tpl-${draft.id ?? "new"}`}
                      blocks={draft.blocks}
                      editable={true}
                      mentionLabel={mentionLabel}
                      mentionAlive={mentionAlive}
                      onOpenMention={noop}
                      mentionItems={mentionItems}
                      resolveUrl={resolveUrl}
                      uploadFile={uploadFile}
                      onBlocks={(blocks) => setDraft((d) => (d ? { ...d, blocks } : d))}
                      onPasteRejected={setPasteError}
                      onFocusTitle={() => nameInputRef.current?.focus()}
                      onCreateWork={noop}
                      canCreateTask={false}
                      canCreateIssue={false}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className={styles.pane}>
              <div className={styles.paneHeader}>
                <div className={styles.head}>
                  <Subtitle2>{selected.name}</Subtitle2>
                  <Badge appearance="tint" color={selected.origin === "system" ? "informative" : "brand"}>
                    {DOCUMENT_KIND_LABEL[selected.kind]}
                  </Badge>
                  <Caption1>{selected.origin === "system" ? "기본 템플릿" : "워크스페이스 템플릿"}</Caption1>
                </div>

                <div className={styles.docMetaRow}>
                  {selected.origin === "system" ? (
                    <span className={styles.docMetaItem}>시스템 기본 서식</span>
                  ) : (
                    <>
                      {selected.createdBy ? (
                        <>
                          <span className={styles.docMetaItem}>작성자 {selected.createdBy}</span>
                          {selected.createdAt ? (
                            <span className={styles.docMetaItem}>({formatDate(selected.createdAt, "numeric")})</span>
                          ) : null}
                          <span className={styles.dot} aria-hidden>
                            ·
                          </span>
                        </>
                      ) : null}
                      <span className={styles.docMetaItem}>
                        {selected.createdBy && selected.createdBy !== selected.updatedBy
                          ? `최종 수정자 ${selected.updatedBy || "—"}`
                          : `수정자 ${selected.updatedBy || "—"}`}
                      </span>
                      {selected.updatedAt ? (
                        <>
                          <span className={styles.dot} aria-hidden>
                            ·
                          </span>
                          <span className={styles.docMetaItem}>수정 {formatDate(selected.updatedAt, "dateTime")}</span>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              <div className={styles.scroll}>
                <div className={styles.editorHost}>
                  <Suspense fallback={<TemplatePreview offer={selected} />}>
                    <DocBlockEditor
                      key={`preview-${selected.id}`}
                      docId={`preview-${selected.id}`}
                      blocks={previewBlocks}
                      editable={false}
                      mentionLabel={mentionLabel}
                      mentionAlive={mentionAlive}
                      onOpenMention={noop}
                      mentionItems={mentionItems}
                      resolveUrl={resolveUrl}
                      uploadFile={uploadFile}
                      onBlocks={noop}
                      onPasteRejected={noop}
                      onFocusTitle={noop}
                      onCreateWork={noop}
                      canCreateTask={false}
                      canCreateIssue={false}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
          ) : (
            <Text className={styles.empty}>왼쪽에서 템플릿을 고르세요.</Text>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={pendingDelete != null}
        title="템플릿을 지울까요?"
        message="이 템플릿을 지웁니다. 이미 만든 문서는 그대로입니다."
        confirmLabel="지우기"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) {
            useCatalogStore.getState().deleteWorkspaceDocTemplate(pendingDelete);
            if (pendingDelete === selectedId) {
              const remaining = shown.filter((o) => o.id !== pendingDelete);
              setSelectedId(remaining[0]?.id ?? offers.find((o) => o.id !== pendingDelete)?.id ?? "");
            }
          }
          setPendingDelete(null);
        }}
      />
    </AppDialog>
  );
}
